// Translation support for the front end.
//
// Strings live in locales/: English in locales/en.js, which is bundled so the
// first render never waits on the network, and every other language in
// locales/<code>.json, fetched the first time it is selected. Keys are flat,
// dotted names ("header.logout") so a translator's file diffs line-for-line
// against en.js. `node scripts/i18n-check.js` lists the keys a translation is
// missing.
//
// In a component:
//
//     import { t, Localized } from './i18n.js';
//     class Foo extends Localized(LitElement) {
//         render() { return html`<button>${t('header.logout')}</button>`; }
//     }
//
// Localized() re-renders the element when the language changes; t() alone is
// enough for code that runs once per render anyway.
//
// Which language is used, first match wins:
//   1. ?lang=<code> in the URL -- lets the LMS index pages pin a language, and
//      lets QA preview a translation that is not yet enabled.
//   2. The signed-in user's saved choice (users/{email}.language), applied by
//      intrivity_app.js once the profile loads.
//   3. This browser's last choice (localStorage).
//   4. The browser's own language list.
//   5. English.
import { unsafeHTML } from 'lit-html/directives/unsafe-html.js';
import { INTRIVITY_LOCALE_CHANGED, escapeHtml } from './utility.js';
import en from './locales/en.js';

export const DEFAULT_LOCALE = 'en';

// Every language the app knows about. `enabled` controls whether it is
// offered in the language picker and picked up automatically from the
// browser; a disabled one is still reachable with ?lang= for review.
// `name` is written in the language itself, as pickers conventionally do.
export const LOCALES = {
    en: { name: 'English', enabled: true },
    es: { name: 'Español', enabled: false },
    de: { name: 'Deutsch', enabled: false },
    fr: { name: 'Français', enabled: false },
    'zh-hans': { name: '简体中文', enabled: false },
    'zh-hant': { name: '繁體中文', enabled: false },
    ja: { name: '日本語', enabled: false },
    ko: { name: '한국어', enabled: false },
};

// Tags browsers send for Chinese that don't name our codes directly. Script
// subtags (zh-Hans-CN, zh-Hant-HK) are handled by prefix in normalize().
const ALIASES = {
    zh: 'zh-hans', 'zh-cn': 'zh-hans', 'zh-sg': 'zh-hans', 'zh-my': 'zh-hans',
    'zh-tw': 'zh-hant', 'zh-hk': 'zh-hant', 'zh-mo': 'zh-hant',
};

const RTL_LANGUAGES = ['ar', 'he', 'fa', 'ur'];
// Korean wraps only at spaces; the browser default breaks between any two
// syllables. word-break is inherited, so this reaches every shadow root.
const KEEP_ALL_LANGUAGES = ['ko'];
const STORAGE_KEY = 'intrivity_locale';

const catalogs = { en };
let currentLocale = DEFAULT_LOCALE;

export const getLocale = () => currentLocale;

export const enabledLocales = () =>
    Object.keys(LOCALES).filter((code) => LOCALES[code].enabled);

export const isKnownLocale = (code) => !!code && Object.prototype.hasOwnProperty.call(LOCALES, code);

// Map a browser or user-supplied tag to one of our codes: exact match, then
// a script prefix ("zh-Hans-CN" -> "zh-hans"), then a region alias
// ("zh-TW" -> "zh-hant"), then the base language ("es-MX" -> "es").
const normalize = (code, { allowDisabled = false } = {}) => {
    if (!code) return undefined;
    const lower = String(code).toLowerCase().replace(/_/g, '-');
    const ok = (c) => isKnownLocale(c) && (allowDisabled || LOCALES[c].enabled);
    if (ok(lower)) return lower;
    const parts = lower.split('-');
    const candidates = [
        parts.slice(0, 2).join('-'),        // zh-hans-cn -> zh-hans
        ALIASES[parts.slice(0, 2).join('-')], // zh-tw -> zh-hant
        ALIASES[parts[0]],                  // zh -> zh-hans
        parts[0],                           // es-mx -> es
    ];
    return candidates.find((c) => c && ok(c));
};

// Our code for a tag such as "zh-TW" or "ES", enabled or not; undefined if
// it isn't a language we know. For validating codes typed by admins.
export const resolveLocale = (code) => normalize(code, { allowDisabled: true });

const readStored = () => {
    try { return localStorage.getItem(STORAGE_KEY); } catch (e) { return null; }
};

const writeStored = (code) => {
    try { localStorage.setItem(STORAGE_KEY, code); } catch (e) { /* private mode */ }
};

const urlLocale = () => {
    try { return new URLSearchParams(window.location.search).get('lang'); } catch (e) { return null; }
};

// The language this visit should start in, before any user profile is known.
export const detectLocale = () => {
    const fromUrl = normalize(urlLocale(), { allowDisabled: true });
    if (fromUrl) return fromUrl;
    const stored = normalize(readStored());
    if (stored) return stored;
    for (const code of (navigator.languages || [navigator.language])) {
        const match = normalize(code);
        if (match) return match;
    }
    return DEFAULT_LOCALE;
};

// True when the URL pins the language, so a saved preference must not
// override it.
export const localeFromUrl = () => !!normalize(urlLocale(), { allowDisabled: true });

const loadCatalog = async (code) => {
    if (catalogs[code]) return catalogs[code];
    const response = await fetch(`/locales/${code}.json`);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    // The hosting SPA rewrite answers a missing file with index.html and a
    // 200, so a bad parse here means "no such file" as often as "bad JSON".
    catalogs[code] = await response.json();
    return catalogs[code];
};

// Switch language. `remember` saves the choice in this browser; pass it only
// for a choice the person made. Resolves to the locale actually in effect,
// which is English if the requested catalog could not be loaded.
export const setLocale = async (code, { remember = false, userChosen = false } = {}) => {
    const target = normalize(code, { allowDisabled: true }) || DEFAULT_LOCALE;
    try {
        await loadCatalog(target);
    } catch (e) {
        console.error(`i18n: could not load locale "${target}", staying on "${currentLocale}"`, e);
        return currentLocale;
    }
    if (remember) writeStored(target);
    if (target === currentLocale) return currentLocale;

    currentLocale = target;
    document.documentElement.lang = target;
    document.documentElement.dir = RTL_LANGUAGES.includes(target) ? 'rtl' : 'ltr';
    document.documentElement.style.wordBreak = KEEP_ALL_LANGUAGES.includes(target) ? 'keep-all' : '';
    window.dispatchEvent(new CustomEvent(INTRIVITY_LOCALE_CHANGED, {
        detail: { locale: target, userChosen },
    }));
    return target;
};

const interpolate = (text, params) =>
    text.replace(/\{(\w+)\}/g, (match, name) =>
        (params && params[name] !== undefined && params[name] !== null) ? String(params[name]) : match);

const lookup = (code, key) => {
    const catalog = catalogs[code];
    return catalog ? catalog[key] : undefined;
};

// Translate `key`, substituting {name} placeholders from `params`.
//
// A catalog entry may be a plain string, or an object of plural forms keyed
// by Intl.PluralRules category ("one", "few", "other", ...) chosen by
// params.count:
//     "groups.memberCount": { "one": "{count} member", "other": "{count} members" }
//
// Falls back to English, then to the key itself, so an untranslated string
// shows up in the UI as something a developer can grep for rather than as
// a blank.
export const t = (key, params) => {
    let entry = lookup(currentLocale, key);
    let entryLocale = currentLocale;
    if (entry === undefined) {
        entry = lookup(DEFAULT_LOCALE, key);
        entryLocale = DEFAULT_LOCALE;
    }
    if (entry === undefined) {
        console.warn(`i18n: missing key "${key}"`);
        return key;
    }
    if (typeof entry === 'object') {
        const count = params && typeof params.count === 'number' ? params.count : 0;
        const category = new Intl.PluralRules(entryLocale).select(count);
        entry = entry[category] ?? entry.other ?? '';
    }
    return interpolate(entry, params);
};

// For a string with inline markup -- a link or <strong> mid-sentence -- so a
// translator can move it within the sentence instead of translating fragments.
// Catalog text is trusted (it ships in this repo); params are escaped, so a
// user's name can go in as {name} safely. Returns a lit directive for use
// inside html``; use tHtmlString() when building a plain HTML string.
export const tHtmlString = (key, params) => {
    const safe = {};
    for (const [name, value] of Object.entries(params || {})) {
        safe[name] = typeof value === 'number' ? value : escapeHtml(value);
    }
    return t(key, safe);
};
export const tHtml = (key, params) => unsafeHTML(tHtmlString(key, params));

// Locale-aware formatting, so dates and numbers follow the chosen language
// instead of the browser's.
export const formatNumber = (value, options) =>
    new Intl.NumberFormat(currentLocale, options).format(value);

export const formatDate = (value, options = { dateStyle: 'medium' }) =>
    new Intl.DateTimeFormat(currentLocale, options).format(value instanceof Date ? value : new Date(value));

// Class mixin: re-render the element whenever the language changes.
export const Localized = (Base) => class extends Base {
    constructor() {
        super();
        this._onLocaleChanged = () => this.requestUpdate();
    }
    connectedCallback() {
        super.connectedCallback();
        window.addEventListener(INTRIVITY_LOCALE_CHANGED, this._onLocaleChanged);
    }
    disconnectedCallback() {
        window.removeEventListener(INTRIVITY_LOCALE_CHANGED, this._onLocaleChanged);
        super.disconnectedCallback();
    }
};
